import {
  BufferAttribute,
  BufferGeometry,
  Clock,
  Color,
  Group,
  NormalBlending,
  PerspectiveCamera,
  Points,
  Scene,
  ShaderMaterial,
  WebGLRenderer,
} from 'three';
import { damp } from './state';

/**
 * The portrait, rebuilt as a particle field. Every particle is one sampled
 * pixel of the photo. They start as a scattered cloud and swirl into place as
 * the section scrolls into view; the pointer pushes them apart and toward the
 * viewer, revealing the depth of the field.
 */

const SIZE = 1.6; // half-extent of the portrait in world units

const vertex = /* glsl */ `
  attribute vec3 aTarget;
  attribute vec3 aScatter;
  attribute vec3 aColor;
  attribute float aSeed;
  uniform float uProgress;
  uniform float uTime;
  uniform float uPointScale;
  uniform float uSize;
  uniform vec2 uMouse;
  uniform float uMouseStrength;
  varying vec3 vColor;
  varying float vAlpha;

  mat2 rot(float a) {
    float c = cos(a), s = sin(a);
    return mat2(c, -s, s, c);
  }

  void main() {
    // Staggered gather: each particle has its own start offset.
    float p = clamp(uProgress * 1.4 - aSeed * 0.4, 0.0, 1.0);
    p = 1.0 - pow(1.0 - p, 3.0);

    vec3 pos = mix(aScatter, aTarget, p);
    pos.xz = rot((1.0 - p) * (2.4 + aSeed * 2.2)) * pos.xz;
    pos.z += sin(uTime * 0.9 + aTarget.x * 3.0 + aTarget.y * 2.0) * 0.025 * p;

    vec2 d = pos.xy - uMouse;
    float f = smoothstep(0.55, 0.0, length(d)) * uMouseStrength * p;
    pos.xy += normalize(d + 1e-5) * f * 0.2;
    pos.z += f * 0.55;

    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    gl_PointSize = uSize * uPointScale / -mv.z * (1.0 + f * 0.8);
    gl_Position = projectionMatrix * mv;

    float edge = smoothstep(1.0, 0.8, length(aTarget.xy) / ${SIZE.toFixed(2)});
    vAlpha = mix(0.55, 1.0, p) * mix(1.0, edge, p);
    vColor = aColor;
  }
`;

const fragment = /* glsl */ `
  uniform vec3 uDeep;
  uniform vec3 uMint;
  varying vec3 vColor;
  varying float vAlpha;

  void main() {
    float d = length(gl_PointCoord - 0.5);
    if (d > 0.5) discard;
    float l = dot(vColor, vec3(0.299, 0.587, 0.114));
    vec3 graded = mix(vColor, mix(uDeep, uMint, l), 0.22);
    gl_FragColor = vec4(graded, vAlpha * smoothstep(0.5, 0.3, d));
  }
`;

export class PortraitScene {
  private renderer: WebGLRenderer;
  private scene = new Scene();
  private camera = new PerspectiveCamera(35, 1, 0.1, 30);
  private clock = new Clock();
  private group = new Group();
  private points?: Points<BufferGeometry, ShaderMaterial>;
  private raf = 0;
  private visible = false;
  private io: IntersectionObserver;
  private ro: ResizeObserver;
  private progress = 0;
  private mouse = { x: 0, y: 0, strength: 0, inside: false };

  constructor(
    private canvas: HTMLCanvasElement,
    private reduced: boolean
  ) {
    this.renderer = new WebGLRenderer({
      canvas,
      antialias: false,
      alpha: true,
    });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.scene.add(this.group);

    this.ro = new ResizeObserver(this.resize);
    this.ro.observe(canvas);
    this.io = new IntersectionObserver(
      ([entry]) => {
        this.visible = !!entry?.isIntersecting;
        if (this.visible) this.loop();
      },
      { rootMargin: '10% 0px' }
    );
    this.io.observe(canvas);

    if (!reduced) {
      canvas.addEventListener('pointermove', this.onPointer);
      canvas.addEventListener('pointerleave', this.onLeave);
    }
  }

  /** Sample the photo into particles and pre-compile the shader. */
  async load(src: string) {
    const img = new Image();
    img.decoding = 'async';
    img.src = src;
    await img.decode();

    const n = window.innerWidth < 768 ? 120 : 170;
    const c = document.createElement('canvas');
    c.width = c.height = n;
    const ctx = c.getContext('2d', { willReadFrequently: true })!;
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    ctx.drawImage(
      img,
      (img.naturalWidth - side) / 2,
      (img.naturalHeight - side) / 2,
      side,
      side,
      0,
      0,
      n,
      n
    );
    const data = ctx.getImageData(0, 0, n, n).data;

    const target: number[] = [];
    const scatter: number[] = [];
    const color: number[] = [];
    const seed: number[] = [];
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        const tx = (x / (n - 1)) * 2 - 1;
        const ty = 1 - (y / (n - 1)) * 2;
        if (tx * tx + ty * ty > 1.04) continue; // circular crop
        const i = (y * n + x) * 4;
        const r = data[i]! / 255;
        const g = data[i + 1]! / 255;
        const b = data[i + 2]! / 255;
        const lum = r * 0.299 + g * 0.587 + b * 0.114;
        target.push(tx * SIZE, ty * SIZE, (lum - 0.5) * 0.18);
        // Scatter on a wide, flattened shell so the swirl reads in 3D.
        const th = Math.random() * Math.PI * 2;
        const rad = 1.4 + Math.random() * 2.6;
        scatter.push(
          Math.cos(th) * rad * 1.3,
          (Math.random() - 0.5) * 3.6,
          Math.sin(th) * rad - 0.5
        );
        color.push(r, g, b);
        seed.push(Math.random());
      }
    }

    const geo = new BufferGeometry();
    geo.setAttribute(
      'position',
      new BufferAttribute(new Float32Array(target), 3)
    );
    geo.setAttribute(
      'aTarget',
      new BufferAttribute(new Float32Array(target), 3)
    );
    geo.setAttribute(
      'aScatter',
      new BufferAttribute(new Float32Array(scatter), 3)
    );
    geo.setAttribute('aColor', new BufferAttribute(new Float32Array(color), 3));
    geo.setAttribute('aSeed', new BufferAttribute(new Float32Array(seed), 1));
    geo.computeBoundingSphere();
    geo.boundingSphere!.radius = 6; // particles travel beyond the target bounds

    const spacing = (SIZE * 2) / n;
    this.points = new Points(
      geo,
      new ShaderMaterial({
        vertexShader: vertex,
        fragmentShader: fragment,
        transparent: true,
        depthWrite: false,
        blending: NormalBlending,
        uniforms: {
          uProgress: { value: this.reduced ? 1 : 0 },
          uTime: { value: 0 },
          uPointScale: { value: 1 },
          uSize: { value: spacing * 1.25 },
          uMouse: { value: [9, 9] },
          uMouseStrength: { value: 0 },
          uDeep: { value: new Color('#325653') },
          uMint: { value: new Color('#cfe9df') },
        },
      })
    );
    this.group.add(this.points);
    this.resize();
    this.renderer.compile(this.scene, this.camera);
    this.renderer.render(this.scene, this.camera);
  }

  private resize = () => {
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    const tan = Math.tan((this.camera.fov * Math.PI) / 360);
    this.camera.position.z =
      (SIZE * 1.06) / tan / Math.min(1, this.camera.aspect);
    this.camera.updateProjectionMatrix();
    if (this.points) {
      const u = this.points.material.uniforms;
      u.uPointScale!.value = (h * this.renderer.getPixelRatio()) / (2 * tan);
      if (this.reduced || !this.visible)
        this.renderer.render(this.scene, this.camera);
    }
  };

  private onPointer = (e: PointerEvent) => {
    const r = this.canvas.getBoundingClientRect();
    const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
    const ny = -(((e.clientY - r.top) / r.height) * 2 - 1);
    const tan = Math.tan((this.camera.fov * Math.PI) / 360);
    const halfH = tan * this.camera.position.z;
    this.mouse.x = nx * halfH * this.camera.aspect;
    this.mouse.y = ny * halfH;
    this.mouse.inside = true;
  };

  private onLeave = () => {
    this.mouse.inside = false;
  };

  private loop = () => {
    cancelAnimationFrame(this.raf);
    if (!this.points || this.reduced) return;
    const tick = () => {
      if (!this.visible) return;
      this.raf = requestAnimationFrame(tick);
      const dt = Math.min(this.clock.getDelta(), 1 / 20);
      this.update(dt);
      this.renderer.render(this.scene, this.camera);
    };
    this.clock.getDelta();
    tick();
  };

  private update(dt: number) {
    const u = this.points!.material.uniforms;
    const vh = window.innerHeight;
    const top = this.canvas.getBoundingClientRect().top;
    // Gather from when the canvas enters until it sits in the upper third.
    const target = Math.min(1, Math.max(0, (vh * 0.95 - top) / (vh * 0.7)));
    this.progress = damp(this.progress, target, 3.2, dt);

    const m = this.mouse;
    m.strength = damp(m.strength, m.inside ? 1 : 0, 4, dt);
    const mu = u.uMouse!.value as number[];
    mu[0] = damp(mu[0]!, m.inside ? m.x : mu[0]!, 10, dt);
    mu[1] = damp(mu[1]!, m.inside ? m.y : mu[1]!, 10, dt);

    u.uProgress!.value = this.progress;
    u.uTime!.value = this.clock.elapsedTime;
    u.uMouseStrength!.value = m.strength;

    // Subtle 3D lean toward the pointer shows off the depth.
    const lx = m.inside ? m.x / SIZE : 0;
    const ly = m.inside ? m.y / SIZE : 0;
    this.group.rotation.y = damp(this.group.rotation.y, lx * 0.22, 3, dt);
    this.group.rotation.x = damp(this.group.rotation.x, -ly * 0.16, 3, dt);
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    this.io.disconnect();
    this.ro.disconnect();
    this.canvas.removeEventListener('pointermove', this.onPointer);
    this.canvas.removeEventListener('pointerleave', this.onLeave);
    this.points?.geometry.dispose();
    this.points?.material.dispose();
    this.renderer.dispose();
  }
}
